import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner, Table } from "reactstrap";

import { atualizarCriterios, exportarResultado, rankingBid, resumoResultado } from "services/bidFreteApi";
import { downloadBlob } from "utils/downloadBlob";
import { FONTE_UI, NIVEIS_ORDEM, NIVEL_UI, REGIOES, coberturaParcial, complementoPeso, economiaPct, erroDaApi, fmtBRL, fmtNumero, fmtPct, listaSiglas, nomeCandidata, ordenarRanking, pesosValidos, rotuloArea } from "../regras";

// Aba Ranking (H4 PO1-2843): seletor de nível (Brasil, região, UF, modal) e
// área, tabela com posição, score, frete simulado, baseline, custo extra,
// economia (R$ e %), frete/cobrado (o custo do score), prazo médio, quanto a
// candidata atende da baseline (NFs e R$ do cobrado), marca de cobertura parcial
// (<90%) e incumbente. Editar os pesos chama PUT /criterios (refaz só o ranking, não
// recalcula frete) e recarrega. Export xlsx via downloadBlob.

function areasLocais(bid, nivel) {
  if (nivel === "BRASIL") return [{ nivel, area: "BR", rotulo: "Brasil" }];
  if (nivel === "REGIAO") {
    const lista = listaSiglas(bid.regioes);
    const base = lista.length ? lista : Object.keys(REGIOES);
    return base.map((r) => ({ nivel, area: r, rotulo: REGIOES[r] || r }));
  }
  if (nivel === "UF")
    return listaSiglas(bid.ufs).map((u) => ({ nivel, area: u, rotulo: u }));
  return listaSiglas(bid.idsModal).map((m) => ({ nivel, area: m, rotulo: `Modal ${m}` }));
}

export default function AbaRanking({ bid, empresaId, recarregar }) {
  const simulada = Boolean(bid && (bid.etapas || []).some((e) => e.etapa === "SIMULACAO" && e.status === "CONCLUIDA"));
  const [nivel, setNivel] = useState((bid && bid.nivelInicial) || "BRASIL");
  const [area, setArea] = useState(null);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [pesoCusto, setPesoCusto] = useState(Number(bid && bid.pesoCusto) || 70);
  const [pesoPrazo, setPesoPrazo] = useState(Number(bid && bid.pesoPrazo) || 30);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [resumo, setResumo] = useState(null);
  const areas = useMemo(() => {
    const daApi = (dados && dados.areas) || [];
    const doNivel = daApi.filter((a) => !a.nivel || a.nivel === nivel);
    return doNivel.length ? doNivel : areasLocais(bid, nivel);
  }, [dados, bid, nivel]);

  // Cards do resultado (GET /resultado/resumo).
  useEffect(() => {
    if (!simulada || !empresaId || !bid) return undefined;
    let ativo = true;
    resumoResultado(empresaId, bid.id)
      .then((r) => {
        if (ativo) setResumo(r);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [simulada, empresaId, bid]);
  const areaEfetiva = area && areas.some((a) => String(a.area) === String(area)) ? area : areas.length ? areas[0].area : null;

  const carregar = useCallback(async () => {
    if (!simulada || !empresaId || !bid) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await rankingBid(empresaId, bid.id, { nivel, area: areaEfetiva });
      setDados(r);
      if (r && r.criterios && !editando) {
        if (r.criterios.pesoCusto != null) setPesoCusto(Number(r.criterios.pesoCusto));
        if (r.criterios.pesoPrazo != null) setPesoPrazo(Number(r.criterios.pesoPrazo));
      }
    } catch (err) {
      setErro(erroDaApi(err, "Não foi possível carregar o ranking.").mensagem);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulada, empresaId, bid, nivel, areaEfetiva]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const candidatas = useMemo(() => ordenarRanking(dados && dados.candidatas), [dados]);

  const salvarPesos = async () => {
    if (!pesosValidos(pesoCusto, pesoPrazo)) {
      setErro("Os pesos precisam ser inteiros e somar 100.");
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await atualizarCriterios(empresaId, bid.id, { pesoCusto, pesoPrazo });
      setEditando(false);
      await carregar();
      if (recarregar) recarregar();
    } catch (err) {
      const e = erroDaApi(err, "Não foi possível atualizar os critérios.");
      setErro(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const exportar = async () => {
    setExportando(true);
    setErro(null);
    try {
      const blob = await exportarResultado(empresaId, bid.id);
      downloadBlob(blob, `bid-frete-${bid.id}.xlsx`);
    } catch (err) {
      setErro("Não foi possível exportar (limite de 300 mil NFs).");
    } finally {
      setExportando(false);
    }
  };

  if (!simulada) return <div className="bf-placeholder">O ranking aparece depois da simulação. Com a baseline concluída, use Executar simulação.</div>;

  const cards = (resumo && resumo.cards) || null;

  return (
    <>
      {cards ? (() => {
        // melhorCenario: { transportadora, tabela, vrFrete, vrBaseline, cobertura, coberturaValor }
        const mc = cards.melhorCenario && typeof cards.melhorCenario === "object" ? cards.melhorCenario : null;
        const pctEco = mc ? economiaPct(mc.vrBaseline, mc.vrFrete) : null;
        return (
        <div className="lf-kpis" data-testid="bf-resumo-resultado">
          <div className="lf-kpi">
            <div className="lf-kpi-label">NFs comparáveis</div>
            <div className="lf-kpi-value">{fmtNumero(cards.qtdNfComparavel)}</div>
            <div className="lf-kpi-sub">
              de {fmtNumero(cards.qtdNf)} · {fmtNumero(cards.qtdNfSemCte)} sem CTe
            </div>
          </div>
          <div className="lf-kpi">
            <div className="lf-kpi-label">Cobrado</div>
            <div className="lf-kpi-value">{fmtBRL(Number(cards.cobrado))}</div>
            <div className="lf-kpi-sub">custo extra {fmtBRL(Number(cards.custoExtraCobrado) || 0)}</div>
          </div>
          <div className="lf-kpi">
            <div className="lf-kpi-label">Melhor cenário</div>
            <div className="lf-kpi-value">{mc ? fmtBRL(Number(mc.vrFrete)) : "—"}</div>
            <div className="lf-kpi-sub" data-testid="bf-melhor-cenario-sub">
              {mc ? `${mc.transportadora || "—"} · atende ${fmtPct(Number(mc.coberturaValor))} do cobrado (${fmtPct(Number(mc.cobertura))} das NFs)` : "sem candidata"}
            </div>
          </div>
          <div className="lf-kpi">
            <div className="lf-kpi-label">Economia</div>
            <div className={`lf-kpi-value ${Number(cards.economia) < 0 ? "bf-text-danger" : "bf-text-success"}`}>{cards.economia != null ? fmtBRL(Number(cards.economia)) : "—"}</div>
            <div className="lf-kpi-sub">{pctEco != null ? fmtPct(pctEco) : "—"} do cobrado nas NFs atendidas</div>
          </div>
        </div>
        );
      })() : null}
      <div className="bf-tab-toolbar">
        <FormGroup>
          <Label>Nível</Label>
          <Input
            type="select"
            value={nivel}
            onChange={(e) => {
              const v = e.target.value;
              setNivel(v);
              setArea(null);
            }}
          >
            {NIVEIS_ORDEM.map((n) => (
              <option key={n} value={n}>
                {NIVEL_UI[n].label}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label>Área</Label>
          <Input
            type="select"
            value={areaEfetiva == null ? "" : String(areaEfetiva)}
            onChange={(e) => {
              const v = e.target.value;
              setArea(v);
            }}
            disabled={nivel === "BRASIL"}
          >
            {areas.map((a) => (
              <option key={String(a.area)} value={String(a.area)}>
                {a.rotulo || rotuloArea(nivel, a.area)}
                {a.qtdCandidatas != null ? ` · ${a.qtdCandidatas} candidata(s)` : ""}
                {a.qtdNf != null ? ` · ${fmtNumero(a.qtdNf)} NFs` : ""}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label>Pesos</Label>
          {editando ? (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <Input
                type="number"
                bsSize="sm"
                style={{ width: 70 }}
                min={0}
                max={100}
                value={pesoCusto}
                aria-label="Peso custo"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setPesoCusto(v);
                  setPesoPrazo(complementoPeso(v));
                }}
              />
              <span className="lf-muted">custo</span>
              <Input
                type="number"
                bsSize="sm"
                style={{ width: 70 }}
                min={0}
                max={100}
                value={pesoPrazo}
                aria-label="Peso prazo"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setPesoPrazo(v);
                  setPesoCusto(complementoPeso(v));
                }}
              />
              <span className="lf-muted">prazo</span>
              <Button color="primary" size="sm" onClick={salvarPesos} disabled={salvando}>
                {salvando ? <Spinner size="sm" /> : "Aplicar"}
              </Button>
              <Button color="secondary" size="sm" outline onClick={() => setEditando(false)} disabled={salvando}>
                Cancelar
              </Button>
            </div>
          ) : (
            <div>
              <span className="lf-kv-value">
                custo {pesoCusto}% · prazo {pesoPrazo}%
              </span>{" "}
              <button type="button" className="lf-link" onClick={() => setEditando(true)}>
                editar
              </button>
            </div>
          )}
        </FormGroup>
        <div style={{ marginLeft: "auto" }}>
          <Button color="info" size="sm" outline onClick={exportar} disabled={exportando}>
            {exportando ? <Spinner size="sm" /> : "Exportar xlsx"}
          </Button>
        </div>
      </div>

      {erro ? <div className="lf-aviso lf-aviso-danger">{erro}</div> : null}

      {loading && !dados ? (
        <div className="lf-empty-state">
          <Spinner size="sm" /> Carregando o ranking...
        </div>
      ) : candidatas.length === 0 ? (
        <div className="lf-empty-state">Nenhuma candidata nesta área.</div>
      ) : (
        <Table className="bf-table" size="sm" responsive hover>
          <thead className="text-primary">
            <tr>
              <th>#</th>
              <th>Transportadora</th>
              <th>Fonte</th>
              <th className="is-number">Score</th>
              <th className="is-number">Frete simulado</th>
              <th className="is-number">Baseline</th>
              <th className="is-number">Custo extra</th>
              <th className="is-number">Economia</th>
              <th className="is-number" title="Frete simulado ÷ cobrado nas NFs que a candidata atende (é o custo usado no score)">Frete / cobrado</th>
              <th className="is-number">Prazo médio</th>
              <th className="is-number" title="Quanto da baseline a candidata atende: R$ do cobrado e NFs">Atende da baseline</th>
            </tr>
          </thead>
          <tbody>
            {candidatas.map((c, i) => {
              const parcial = Boolean(c.coberturaParcial) || coberturaParcial(c.cobertura);
              const eco = c.vrEconomia != null ? Number(c.vrEconomia) : Number(c.vrBaseline) - Number(c.vrFrete);
              const pct = economiaPct(c.vrBaseline, c.vrFrete);
              const fonte = FONTE_UI[c.fonte] || FONTE_UI.TABELA;
              return (
                <tr key={`${c.idTransportadora}-${c.idFreteTabela || c.fonte}`} className={`${c.ehIncumbente ? "is-destaque" : ""}${parcial ? " is-parcial" : ""}`.trim()}>
                  <td>{c.posicao != null ? c.posicao : i + 1}</td>
                  <td>
                    <strong>{nomeCandidata(c)}</strong>{" "}
                    {c.ehIncumbente ? (
                      <span className="lf-badge lf-badge-info" title="Transportadora atual">
                        incumbente
                      </span>
                    ) : null}{" "}
                    {parcial ? (
                      <span className="lf-badge lf-badge-warning" title="Cobertura abaixo de 90%">
                        cobertura parcial
                      </span>
                    ) : null}
                    {c.tabela || c.idFreteTabela ? <div className="lf-muted">{c.tabela || `tabela ${c.idFreteTabela}`}</div> : null}
                  </td>
                  <td>
                    <span className={`lf-badge lf-badge-${fonte.tone}`}>{fonte.label}</span>
                  </td>
                  <td className="is-number">{c.score != null ? fmtNumero(c.score, 1) : "—"}</td>
                  <td className="is-number">{fmtBRL(Number(c.vrFrete))}</td>
                  <td className="is-number">{fmtBRL(Number(c.vrBaseline))}</td>
                  <td className="is-number">{c.vrComplementar != null ? fmtBRL(Number(c.vrComplementar)) : "—"}</td>
                  <td className={`is-number ${Number.isFinite(eco) && eco < 0 ? "bf-text-danger" : "bf-text-success"}`}>
                    {Number.isFinite(eco) ? fmtBRL(eco) : "—"}
                    {pct != null ? <div className="lf-muted">{fmtPct(pct)}</div> : null}
                  </td>
                  <td className="is-number">{Number(c.vrBaseline) > 0 ? fmtPct(Number(c.vrFrete) / Number(c.vrBaseline)) : "—"}</td>
                  <td className="is-number">{c.prazoMedioDias != null ? `${fmtNumero(c.prazoMedioDias, 1)} d` : "—"}</td>
                  <td className="is-number">
                    {c.coberturaValor != null ? `${fmtPct(Number(c.coberturaValor))} do cobrado` : "—"}
                    <div className="lf-muted">
                      {c.cobertura != null ? fmtPct(Number(c.cobertura)) : "—"} das NFs
                      {c.qtdCalculada != null && c.qtdNf != null ? ` (${fmtNumero(c.qtdCalculada)} / ${fmtNumero(c.qtdNf)})` : ""}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}
